import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { auth as authApi, impersonation as impersonationApi, type ImpersonationState } from '@/api/endpoints';
import type { User } from '@/types';

interface AuthCtx {
  user: User | null;
  passwordChangeRequired: boolean;
  loading: boolean;
  error: string | null;
  /**
   * Gdy niepuste — `user` to konto, w które wcielił się administrator opisany tym obiektem.
   * Wszystkie uprawnienia w `user.permissions` są uprawnieniami konta docelowego, więc UI
   * renderuje się dokładnie tak, jak widzi je ten użytkownik.
   */
  impersonation: ImpersonationState | null;
  /** Wejście w konto użytkownika; `password` to hasło zalogowanego administratora. */
  wcielSie: (userId: number, password: string) => Promise<void>;
  /** Powrót na własne konto. */
  zakonczImpersonacje: () => Promise<void>;
  /**
   * Login dwustopniowy z 2FA: pierwsze wywołanie bez {@code totpCode}; jeśli user ma 2FA,
   * promise rzuca błąd typu {@link TwoFactorRequiredError} — frontend Login.tsx odbiera to
   * i pokazuje pole na code, po czym retry z totpCode.
   */
  login: (login: string, password: string, totpCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  zmienPassword: (stare: string | null, updated: string) => Promise<void>;
}

/** Specjalny błąd rzucany przez {@code login()} gdy backend prosi o TOTP. */
export class TwoFactorRequiredError extends Error {
  /** True gdy code był podany ale błędny (drugi krok), false gdy w ogóle nie podano (pierwszy krok). */
  readonly invalidCode: boolean;
  constructor(invalidCode: boolean, message: string) {
    super(message);
    this.invalidCode = invalidCode;
    this.name = 'TwoFactorRequiredError';
  }
}

const Ctx = createContext<AuthCtx | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Wspólne wchłonięcie odpowiedzi o sesji. Response ma dwa kształty:
   * {user, passwordChangeRequired, impersonation} albo czysty User (z endpointu logowania).
   */
  const applySession = useCallback((res: unknown) => {
    if (res && typeof res === 'object' && 'user' in res) {
      const s = res as { user: User; passwordChangeRequired?: boolean; impersonation?: ImpersonationState | null };
      setUser(s.user);
      setPasswordChangeRequired(Boolean(s.passwordChangeRequired));
      setImpersonation(s.impersonation ?? null);
    } else {
      setUser(res as User);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      applySession(await authApi.me());
    } catch {
      setUser(null);
      setPasswordChangeRequired(false);
      setImpersonation(null);
    } finally {
      setLoading(false);
    }
  }, [applySession]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Miękkie odświeżenie uprawnień po powrocie do karty. Gdy admin zmieni macierz uprawnień,
  // aktywna sesja użytkownika dostanie nowe user.permissions bez twardego przeładowania —
  // serwer /api/auth/me liczy je na żywo z macierzy. W razie błędu NIE wylogowujemy
  // (zostawiamy bieżący stan) — inaczej przejściowy błąd sieci wyrzucałby usera.
  useEffect(() => {
    const soft = async () => {
      try {
        const res = await authApi.me();
        if (res && typeof res === 'object' && 'user' in res) applySession(res);
      } catch { /* przejściowy błąd — nie ruszamy stanu */ }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') void soft(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [applySession]);

  const login = useCallback(async (u: string, p: string, totpCode?: string) => {
    setError(null);
    try {
      const me = await authApi.login(u, p, totpCode);
      setUser(me);
      // Po zalogowaniu sprawdź czy user ma wymaganą zmianę hasła
      await refresh();
    } catch (e) {
      const err = e as { status?: number; body?: { error?: string; requires2fa?: boolean; message?: string } };
      // 401 z requires2fa=true: backend prosi o code 2FA. Rzucamy specjalny błąd, który
      // Login.tsx przechwytuje żeby pokazać pole na TOTP. Nie ustawiamy `error` —
      // to nie jest błąd "do wyświetlenia", tylko sygnał kontynuacji flow.
      if (err.status === 401 && err.body?.requires2fa) {
        const invalidCode = err.body.error === 'invalid_totp';
        throw new TwoFactorRequiredError(
          invalidCode,
          err.body.message ?? (invalidCode ? 'Nieprawidłowy kod 2FA' : 'Wymagany kod 2FA'));
      }
      setError(err.status === 401 ? 'Błędny login lub hasło' : 'Błąd logowania');
      throw e;
    }
  }, [refresh]);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setPasswordChangeRequired(false);
    setImpersonation(null);
  }, []);

  const wcielSie = useCallback(async (userId: number, password: string) => {
    applySession(await impersonationApi.start(userId, password));
  }, [applySession]);

  const zakonczImpersonacje = useCallback(async () => {
    try {
      applySession(await impersonationApi.stop());
    } catch (e) {
      // Sesja administratora mogła w międzyczasie paść (konto zablokowane, wygaśnięcie) —
      // wtedy backend ją unieważnia i jedyne sensowne wyjście to ekran logowania.
      if ((e as { status?: number }).status === 401) {
        setUser(null);
        setImpersonation(null);
      }
      throw e;
    }
  }, [applySession]);

  const zmienPassword = useCallback(async (stare: string | null, updated: string) => {
    await authApi.changePassword(stare, updated);
    setPasswordChangeRequired(false);
  }, []);

  return (
    <Ctx.Provider value={{ user, passwordChangeRequired, loading, error, impersonation,
                           login, logout, refresh, zmienPassword, wcielSie, zakonczImpersonacje }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be within AuthProvider');
  return ctx;
}
